import React, { useRef, useState } from 'react';
import { AdminWriteAccess, useAdminAccess } from 'contexts/adminAccess';
import get from 'lodash/get';
import classNames from 'classnames';
import { Form } from 'react-final-form';
import { FormApi } from 'final-form';
import {
  FieldProps,
  InjectedFieldProps,
  useDataProvider,
  useNotify,
  useRefresh,
} from 'react-admin';

import makeStyles from '@material-ui/core/styles/makeStyles';

import SubmitButton from 'components/buttons/SubmitButton';
import { Input } from 'components/form';

import { RewardedActionDto } from '../../types';

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    alignItems: 'center',
    minWidth: 190,
    boxSizing: 'border-box',
    gap: '8px',
  },
  input: {
    width: 74,
    height: 44,
    boxSizing: 'border-box',
    padding: theme.spacing(1.5),
    textAlign: 'right',
  },
  button: {
    width: 60,
    height: 44,
    borderRadius: 8,
    textTransform: 'none',
    boxSizing: 'border-box',
    padding: 0,
    display: 'none',
    '&:hover': {
      display: 'flex',
    },
  },
  focused: {
    display: 'flex',
  },
  inputRoot: {
    marginTop: 0,
  },
}));

const onlyNumbers = (value: string) => value.replace(/[^0-9]*/g, '');

type EditableNumberFormValues = {
  editableValue?: number | string;
  newQty?: number | string;
  change?: boolean;
};

type EditableActionPointsFieldProps = Omit<FieldProps, 'source'> &
  InjectedFieldProps & {
    record?: RewardedActionDto;
    source: keyof RewardedActionDto;
  };

const EditableActionPointsField = ({ record, source }: EditableActionPointsFieldProps) => {
  const { canWrite } = useAdminAccess();
  const classes = useStyles();

  const notify = useNotify();
  const dataProvider = useDataProvider();
  const refresh = useRefresh();

  const formRef = useRef<FormApi<EditableNumberFormValues>>();
  const [focused, setFocused] = useState<boolean>(false);

  const handleClickStopPropagation = (event: React.SyntheticEvent) => event.stopPropagation();

  const handleInputFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    handleClickStopPropagation(event);
    setFocused(true);
  };

  const handleSubmitForm = async (values: EditableNumberFormValues) => {
    if (!canWrite) return;
    if (!record?.id) {
      notify('Unable to update action', 'warning');
      return;
    }

    try {
      const finalValue = values?.change ? Number(values.newQty) : Number(values.editableValue ?? 0);

      const formData = new FormData();

      formData.append('title', record?.title?.trim() ?? '');
      formData.append('description', record?.description?.trim() ?? '');
      formData.append('pointsAwarded', String(finalValue ?? 0));
      formData.append('repeatable', record?.repeatable ? 'true' : 'false');
      formData.append('type', record?.type ?? '');
      formData.append('ctaUrl', record?.ctaUrl ?? '');
      formData.append('ctaText', record?.ctaText ?? '');
      formData.append('ctaId', record?.ctaId ?? '');
      formData.append('icon', record?.iconUrl ?? '');

      await dataProvider.update('rewardsAction', {
        id: get(record, 'id'),
        data: formData,
      });

      formRef?.current?.change('editableValue', finalValue);
      // Keep the new value as the baseline so blur resets do not revert it
      formRef?.current?.initialize({ editableValue: finalValue });
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : 'An error occurred while updating rewards action';
      notify(errorMessage, 'error');
    } finally {
      refresh();
    }
  };

  const handleInputBlur = () => {
    setTimeout(() => {
      setFocused(false);

      try {
        formRef.current?.reset();
      } catch (error) {
        console.log('error --->', error);
      }
    }, 200);
  };

  return (
    <Form<EditableNumberFormValues>
      onSubmit={handleSubmitForm}
      initialValues={{
        editableValue: get(record, source, 0) || 0,
      }}
      render={({ handleSubmit, submitting, form }) => {
        formRef.current = form;

        return (
          <form onSubmit={handleSubmit}>
            <div className={classes.root}>
              <Input
                name="editableValue"
                inputProps={{
                  variant: 'outlined',
                  InputProps: { classes: { input: classes.input } },
                  onFocus: handleInputFocus,
                  onBlur: handleInputBlur,
                  onClick: handleClickStopPropagation,
                }}
                classes={{ input: classes.inputRoot }}
                disabled={submitting || !canWrite}
                formatValue={onlyNumbers}
              />
              <AdminWriteAccess>
                <SubmitButton
                  className={classNames(classes.button, focused && classes.focused)}
                  onClick={handleClickStopPropagation}
                  label="Save"
                  submitting={submitting}
                  size="medium"
                />
              </AdminWriteAccess>
            </div>
          </form>
        );
      }}
    />
  );
};

export default EditableActionPointsField;
